import { useEffect, useRef, useState, type RefObject } from 'react';

type UseHomeInViewSectionOptions = {
  rootMargin?: string;
  threshold?: number;
  /** Mount children only after the section nears the viewport. */
  lazy?: boolean;
};

type UseHomeInViewSectionResult = {
  sectionRef: RefObject<HTMLElement | null>;
  inView: boolean;
  mounted: boolean;
};

/**
 * Intersection observer for below-fold sections — defers heavy subtree until scroll proximity.
 */
export function useHomeInViewSection(
  options: UseHomeInViewSectionOptions = {},
): UseHomeInViewSectionResult {
  const { rootMargin = '320px 0px', threshold = 0, lazy = true } = options;
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(!lazy);
  const [mounted, setMounted] = useState(!lazy);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = Boolean(entry?.isIntersecting);
        setInView(visible);
        if (visible) setMounted(true);
      },
      { rootMargin, threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, threshold]);

  return { sectionRef, inView, mounted };
}
